import { useMemo } from 'react'
import { DragDropHandlers } from '@features/file-explorer/types'

interface FolderDragHandlersOptions {
  folderId: string | null // null for root folder
  dragDropHandlers: DragDropHandlers
  enabled?: boolean // Optional flag to enable/disable (e.g., only for folders, not documents)
  targetPrefix?: string // Optional prefix for the target key (e.g., 'tree-folder', 'folder')
}

export function useFolderDragHandlers({
  folderId,
  dragDropHandlers,
  enabled = true,
  targetPrefix = 'folder'
}: FolderDragHandlersOptions) {
  const folderDropTarget = useMemo(() => {
    return folderId === null ? `${targetPrefix}-root` : `${targetPrefix}-${folderId}`
  }, [folderId, targetPrefix])

  const isDragOverThisFolder = dragDropHandlers.dragOverTarget === folderDropTarget

  const handleDragEnter = useMemo(() => {
    if (!enabled) return undefined

    return (e: React.DragEvent) => {
      if (dragDropHandlers.isDragging) {
        e.preventDefault()

        // Prevent dropping a folder into itself
        if (dragDropHandlers.draggedItem &&
            dragDropHandlers.draggedItem.type === 'Folder' &&
            dragDropHandlers.draggedItem.id === folderId) {
          return
        }

        // Don't stop propagation - let it bubble
        dragDropHandlers.setDragTarget(folderDropTarget)
      }
    }
  }, [enabled, dragDropHandlers, folderDropTarget, folderId])

  const handleDragOver = useMemo(() => {
    if (!enabled) return undefined

    return (e: React.DragEvent) => {
      if (dragDropHandlers.isDragging) {
        e.preventDefault()

        // Prevent dropping a folder into itself
        if (dragDropHandlers.draggedItem &&
            dragDropHandlers.draggedItem.type === 'Folder' &&
            dragDropHandlers.draggedItem.id === folderId) {
          return
        }

        // Don't stop propagation
        // Keep setting the target to maintain highlight even when moving over children
        if (dragDropHandlers.dragOverTarget !== folderDropTarget) {
          dragDropHandlers.setDragTarget(folderDropTarget)
        }
      }
    }
  }, [enabled, dragDropHandlers, folderDropTarget, folderId])

  const handleDragLeave = useMemo(() => {
    if (!enabled) return undefined

    return (e: React.DragEvent) => {
      if (dragDropHandlers.isDragging) {
        e.preventDefault()
        // Don't stop propagation

        // Only clear if we're actually leaving the folder, not just moving between child elements
        const relatedTarget = e.relatedTarget as Node
        if (dragDropHandlers.dragOverTarget === folderDropTarget &&
            (!relatedTarget || !e.currentTarget.contains(relatedTarget))) {
          dragDropHandlers.setDragTarget(null)
        }
      }
    }
  }, [enabled, dragDropHandlers, folderDropTarget])

  const handleDrop = useMemo(() => {
    if (!enabled) return undefined

    return (e: React.DragEvent) => {
      if (dragDropHandlers.isDragging) {
        e.preventDefault()
        e.stopPropagation()
        dragDropHandlers.handleDrop(e, folderId)
        dragDropHandlers.setDragTarget(null)
      }
    }
  }, [enabled, dragDropHandlers, folderId])

  return {
    isDragOverThisFolder,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop
  }
}
